"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { NovilotMark } from "@/components/brand/NovilotMark";
import { GoogleAuthButton } from "@/components/auth/GoogleAuthButton";
import { MarketingLanguageSwitcher } from "@/components/i18n/MarketingLanguageSwitcher";
import { useI18n } from "@/components/providers/I18nProvider";
import { usePathname } from "next/navigation";
import { localizedMarketingPath, marketingLocaleFromPath } from "@/lib/i18n/marketingLocale";
import { reviewReturnPath, reviewReturnWorkspace } from "@/lib/editorial/reviewReturn";
import { billingReturnCampaign, billingReturnTo } from "@/lib/authReturnTo";
import { marketingCampaignSource, persistSignupAttribution, readSignupAttribution } from "@/lib/signupAttribution";
import { workspaceIntent, type WorkspaceIntent } from "@/lib/workspaceRouting";

const OAUTH_ERRORS: Record<string, string> = {
  google_unavailable: "Google sign-in is not available right now. Use your email and password.",
  google_denied: "Google sign-in was cancelled.",
  google_state: "That Google sign-in attempt expired. Please try again.",
  google_exchange: "We could not complete Google sign-in. Please try again.",
  google_unverified: "That Google account has no verified email. Use email and password instead.",
};

export function SignInForm({ googleEnabled = false, initialNext = "/dashboard", initialIntent = null, initialCampaign }: { googleEnabled?: boolean; initialNext?: string; initialIntent?: WorkspaceIntent | null; initialCampaign?: string }) {
  const router = useRouter();
  const { t, translateMarketing: localize } = useI18n();
  const pathname = usePathname() || "/sign-in";
  const locale = marketingLocaleFromPath(pathname) ?? "en";
  const localizedPath = (value: string) => localizedMarketingPath(value, locale);

  /**
   * Read after hydration rather than with useSearchParams().
   *
   * Calling that hook here made the whole component bail to the page's
   * Suspense fallback during static rendering, so /sign-in shipped eight
   * characters of HTML — "Loading…" — with no heading and no form. It is an
   * indexable page in the sitemap, and that is all a crawler ever saw.
   *
   * The server supplies the invitation destination so the sign-up link has the
   * correct role even before hydration. Informational banners can load later.
   */
  const [params, setParams] = useState<{
    next: string;
    verifyError: string | null;
    oauthError: string | null;
    intent: WorkspaceIntent | null;
    campaign?: string;
  }>({ next: initialNext, verifyError: null, oauthError: null, intent: initialIntent, campaign: marketingCampaignSource(initialCampaign) });

  const [ready, setReady] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    setParams({
      next: q.get("next") || initialNext,
      verifyError: q.get("verify_error"),
      oauthError: OAUTH_ERRORS[q.get("error") ?? ""] || null,
      intent: workspaceIntent(q.get("intent")) ?? initialIntent,
      campaign: marketingCampaignSource(q.get("src") ?? undefined) ?? billingReturnCampaign(q.get("next")) ?? marketingCampaignSource(initialCampaign),
    });
    setReady(true);
  }, [initialNext, initialIntent, initialCampaign]);

  const { next: nextDefault, verifyError, oauthError, intent, campaign } = params;
  useEffect(() => {
    if (campaign) persistSignupAttribution({ ...readSignupAttribution(), source: campaign });
  }, [campaign]);
  const signupIntent = reviewReturnWorkspace(nextDefault) ?? intent;
  const signupParams = new URLSearchParams({ ...(billingReturnTo(nextDefault) ? { next: billingReturnTo(nextDefault)! } : {}), ...(signupIntent ? { intent: signupIntent } : {}), ...(campaign ? { src: campaign } : {}) });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/sign-in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, next: billingReturnTo(nextDefault), intent }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        redirect?: string;
      };
      if (!res.ok) {
        setError(localize(typeof data.error === "string" ? data.error : "Could not sign in"));
        setLoading(false);
        return;
      }
      // Preserve review invitations across password sign-in without introducing
      // an arbitrary return URL/open redirect into the existing auth flow.
      const reviewReturn = reviewReturnPath(nextDefault);
      const dest = reviewReturn || data.redirect || "/dashboard";
      router.push(dest);
      router.refresh();
    } catch {
      setError(t("auth.genericError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 gap-6 relative">
      <div className="absolute top-5 right-5">
        <MarketingLanguageSwitcher compact />
      </div>
      <div className="flex items-center gap-2">
        <NovilotMark className="w-6 h-6 text-accent" />
        <span className="text-xl font-semibold">Novilot</span>
      </div>

      {verifyError && (
        <div className="w-full max-w-sm rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          {localize("That verification link was")} {localize(verifyError === "expired" ? "expired" : "invalid")}. {localize("Sign in and you can resend a fresh one from the next screen.")}
        </div>
      )}

      {oauthError && (
        <div className="w-full max-w-sm rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          {localize(oauthError)}
        </div>
      )}

      <form
        method="post"
        action={localizedPath("/sign-in")}
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 p-6 border border-border rounded-xl bg-card shadow-sm"
      >
        <h1 className="text-lg font-semibold text-center">{t("auth.signIn.title")}</h1>

        <noscript>
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            {localize("Enable JavaScript to sign in securely.")}
          </p>
        </noscript>

        {error && (
          <p className="text-sm text-destructive text-center" role="alert" aria-live="polite">
            {error}
          </p>
        )}

        {googleEnabled && (
          <>
            <GoogleAuthButton label={t("auth.signIn.continueGoogle")} workspaceIntent={intent ?? undefined} next={billingReturnTo(nextDefault)} campaignSource={campaign} />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t("auth.signIn.or")}
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            {t("auth.signIn.email")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            dir="ltr"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">
            {t("auth.signIn.password")}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            dir="ltr"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !ready}
          className="w-full py-2.5 text-sm font-medium rounded-lg bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-50"
        >
          {!ready ? localize("Preparing sign-in…") : loading ? t("auth.signIn.loading") : t("auth.signIn.submit")}
        </button>

        <p className="text-center text-sm text-muted-foreground">
          {t("auth.signIn.noAccount")} {" "}
          <Link href={localizedPath(`/sign-up${signupParams.size ? `?${signupParams}` : ""}`)} className="text-accent font-medium hover:underline">
            {t("auth.signIn.create")}
          </Link>
        </p>
      </form>
    </div>
  );
}
